import { http } from "./http";
import type { LegalMeta, Role } from "./AuthService";
import { getToken } from "./authStore";
import { BASE_URL } from "../data/constants";

/**
 * Kontoinställningar. Speglar api/accountRoutes.js.
 *
 * Alla anrop gäller den inloggade användaren. Det finns med flit ingen
 * funktion här som tar ett användar-id.
 */

export type AccountSettings = {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    yearOfBirth: number | null;
    bio: string | null;
    avatarUrl: string | null;
    role: Role;
    createdAt: string;
    emailVerifiedAt: string | null;
    emailVerified: boolean;
    hasPassword: boolean;
    termsVersion: string | null;
    termsAcceptedAt: string | null;
  };
  preferences: NotificationPreferences;
  legal: LegalMeta;

  /** Falskt när servern saknar Graph-konfiguration. Se api/mail.js. */
  mailConfigured: boolean;
};

export type NotificationPreferences = {
  checkIn: boolean;
  onMyWay: boolean;
  venueTrack: boolean;
  follows: boolean;
  postLike: boolean;
  /** Vänner som ska ut i kväll. Opt-in — servern defaultar den till false. */
  goingOut: boolean;
  pushEnabled: boolean;
};

export type ProfileInput = {
  firstName?: string;
  lastName?: string;
  bio?: string;
  yearOfBirth?: number | null;
};

export function getSettings() {
  return http.get<AccountSettings>("/me/settings");
}

export function updateProfile(input: ProfileInput) {
  return http.patch<AccountSettings["user"]>("/me", input);
}

export function updateNotificationPreferences(input: Partial<NotificationPreferences>) {
  return http.patch<{ preferences: NotificationPreferences }>(
    "/me/notification-preferences",
    input
  );
}

export function changePassword(input: { currentPassword: string; newPassword: string }) {
  return http.post<{ ok: true }>("/me/password", input);
}

export function startEmailChange(input: { newEmail: string; currentPassword: string }) {
  return http.post<{ ok: true; pendingEmail: string }>("/me/email-change", input);
}

export function resendVerification() {
  return http.post<{ ok: true; alreadyVerified?: boolean }>("/me/resend-verification");
}

export function deleteAccount(input: { currentPassword: string }) {
  return http.delete<{ ok: true }>("/me", input);
}

/**
 * Laddar ner exporten som fil.
 *
 * Går inte genom http.ts: svaret är en fil och inte JSON, och den ska
 * hamna i användarens nedladdningar och inte i en variabel. Blob-URL:en
 * frigörs direkt efter klicket — annars ligger hela exporten kvar i minnet
 * så länge fliken är öppen.
 */
export async function downloadExport(): Promise<void> {
  const token = getToken();

  const res = await fetch(`${BASE_URL}/me/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) throw new Error("export_failed");

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);

  const filename =
    res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ??
    "beatspot-export.json";

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}
